import { ActionModal } from '@kcftech/design-system';
import {
    ICorporationResponse,
    IdentityProviderCreateRequest,
} from '@kcftech/prometheus-client';
import { CorporationDropdown } from 'components/dropdown/CorporationDropdown';
import { Label } from 'components/forms/Label';
import { TextInput } from 'components/forms/TextInput';
import { ValidatableLabel } from 'components/forms/ValidatableLabel';
import { sdClient } from 'contexts/SDKClientContext';
import { Controller, RegisterOptions, useForm } from 'react-hook-form';
import { excludeNullAndUndefined } from 'utils/array';
import { USERNAME_EMAIL_REGEX } from 'utils/domainRegex';

export type AddIdentityProviderModalProps = Readonly<{
    onCreate: () => void;
    onClose: () => void;
}>;

const maxIdentityProviderNameLength = 100;

const nameRules: RegisterOptions = {
    required: 'Identity provider name is required',
    maxLength: {
        value: maxIdentityProviderNameLength,
        message: `Identity Provider name cannot be greater than ${maxIdentityProviderNameLength} characters`,
    },
};

const corporationRules: RegisterOptions = {
    required: 'Please select a corporation',
};

const usernameIdentifiersRules: RegisterOptions = {
    validate: (value: string | undefined) => {
        if (!value) {
            return 'Username identifier is required';
        }
        const errorMessages = excludeNullAndUndefined(
            value.split(',').map((username) => {
                if (username.length > maxIdentityProviderNameLength) {
                    return 'Username identifier is too long';
                }
                if (!USERNAME_EMAIL_REGEX.test(username)) {
                    return 'Please enter a valid username identifier';
                }
                return undefined;
            }),
        );
        if (errorMessages.length > 0) {
            return errorMessages[0];
        }
        return undefined;
    },
};

type IdentityProviderForm = {
    Name: string;
    UsernameIdentifiers: string;
    corporation: ICorporationResponse;
};

export function AddIdentityProviderModal({
    onCreate,
    onClose,
}: AddIdentityProviderModalProps) {
    const {
        control,
        register,
        handleSubmit,
        formState: { errors, isSubmitted, isValid, isValidating, isSubmitting },
    } = useForm<IdentityProviderForm>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
    });

    const onSave = async (data: IdentityProviderForm) => {
        await sdClient.createIdentityProvider({
            body: new IdentityProviderCreateRequest({
                CorporationId: data.corporation.Id,
                Name: data.Name,
                UsernameIdentifiers: data.UsernameIdentifiers.split(','),
            }),
        });
        onCreate();
        return true;
    };

    return (
        <ActionModal
            onAction={handleSubmit(onSave)}
            actionable={(!isSubmitted || isValid) && !(isValidating || isSubmitting)}
            onClose={onClose}
            title="Create Identity Provider"
            actionButtonText="Create"
            closeButtonText="Cancel"
        >
            <ValidatableLabel
                label="Identity Provider name"
                className="mb-20"
                error={errors.Name}
                required
            >
                <TextInput
                    placeholder="Enter the identity provider name from Cognito (case sensitive)"
                    invalid={errors.Name !== undefined}
                    {...register('Name', nameRules)}
                />
            </ValidatableLabel>
            <ValidatableLabel
                label="Username Identifiers"
                className="mb-20"
                error={errors.UsernameIdentifiers}
                required
            >
                <TextInput
                    placeholder="Enter username identifier separated by commas (example @domain.name)"
                    invalid={errors.UsernameIdentifiers !== undefined}
                    {...register('UsernameIdentifiers', usernameIdentifiersRules)}
                />
            </ValidatableLabel>
            <Label label="Corporation name" required>
                <Controller
                    name="corporation"
                    control={control}
                    rules={corporationRules}
                    render={({ field: { value, onChange } }) => (
                        <CorporationDropdown value={value} onChange={onChange} />
                    )}
                />
            </Label>
        </ActionModal>
    );
}
